<template>
  <div class="opc-zone-bundle__main">
    <p class="opc-zmone__form-label">
      {{ $t('billing.zone_pro_plus.select_local_number_title') }}
      <zm-tooltip
        :content="$t('billing.zone.attendee_info')"
        disallow-learn-about
        aria-label="Learn more about numbers"
        placement="top"
        @show="handleAttendeeTipShow"
      >
        <tips-icon type="info" data-testid="checkout-button-121" aria-label="Learn more about numbers" />
      </zm-tooltip>
    </p>
    <license-tip type="proplus" :number="selectedNumber" :additionalSpans='additionalSpans'
      @click='handleLicenseTipsClick' />
    <InputNumberWithPopover class="opc-zmone__quantity-selector" v-model="selectedNumber" size="lg"
      controls-position="both" :min="minHost" :max="maxHost" @change="updatePlanLicense"
      :label="trackingPlanName + ' Select the number of users'"
      :data-testid="`checkout-input-license-${currentEditPlan ? currentEditPlan.plan_code : ''}`"
    />
    <!-- banner -->
    <zm-banner show-close class="reason-tips" v-if="licenseTips" type="info">
      <p v-html="licenseTips"></p>
    </zm-banner>
    <appeal-cycle class='appeal-cycle' :cycle="i18n_cycle(planBillInfo.billing_cycle)" :payment="planBillInfo.payment"
      @click='handleAppealCycle' />
  </div>
</template>

<script>
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import { mapGetters, mapMutations } from 'vuex';
import AppealCycle from '@/plans/ZOOM_PHONE/component/appeal-cycle.vue';
import { computedCallingPlans, computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import {
  BILLING_CYCLE,
  planTypes,
  ZOOM_ONE_PRO_PLUS_MAX_LICENSE,
} from '@/common/constants';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import LicenseTip from '@/plans/ZOOM_MEETING/license-tip.vue';
import plansMixin from '@/plans/mx/plan';
import { validDownSellLicense } from '@/utils/validate-downsell';
import { dateTimeFormat } from '@/utils/dateformat';
import {
  ZOOM_WEBINARS_PLUS,
  ZOOM_WORKPLACE,
  zoomSessionsRebrandingReg,
  ZoomWorkplaceRebrandingReg,
} from '@zoom/billing-rebranding';

export default {
  components: { LicenseTip, AppealCycle, InputNumberWithPopover },
  mixins: [plansMixin],
  data() {
    return {
      selectedNumber: 1,
      trackingPlanName: 'zoom workplace pro plus',
    }
  },
  computed: {
    ...mapGetters(['boughtPlans', 'currentEditPlan', 'getSelectedPlans', 'basicConfig', 'fromCampaignLink', 'plansofBizBundle']),
    minHost() {
      return this.minHostAndReason.min;
    },
    maxHost() {
      const boughtProp = this.boughtPlans.find(p => p.type === planTypes.ZONE_PRO_PLUS);
      const boughtLicense = boughtProp?.cartInfo?.selected_number;
      return boughtLicense > ZOOM_ONE_PRO_PLUS_MAX_LICENSE ? boughtLicense : ZOOM_ONE_PRO_PLUS_MAX_LICENSE;
    },
    licenseTips() {
      return this.minHostAndReason.reason;
    },
    minHostAndReason() {
      let min = 1, reason = '';

      const { needAssignQuantity, planExpireTime } = validDownSellLicense({ quantity: this.selectedNumber });
      if (needAssignQuantity > 0) {
        reason = planExpireTime
          ? this.$t('billing.error.downgrade_alert_info', [needAssignQuantity, dateTimeFormat(planExpireTime)])
          : this.$t('billing.error.downgrade_alert_info_no_date', [needAssignQuantity])
      }

      // disable downsell
      if (this.currentEditPlan?.cartInfo?.selected_number === this.selectedNumber) {
        if (this.disableDownSell) {
          min = this.currentEditPlan?.cartInfo?.selected_number ?? 1;
          reason = this.$t('billing.error.can_not_bought_plan_tips');
        }

        if (this.forNonStandardPriceTips) {
          min = this.currentEditPlan?.cartInfo?.selected_number ?? 1;
          reason = this.forNonStandardPriceTips;
        }
      }

      // add on plan
      const maxAddonPlanLicense = this.plansofBizBundle.maxLicensesAddonPlan?.cartInfo?.selected_number;

      if (maxAddonPlanLicense >= this.selectedNumber && this.selectedNumber > 1) {
        min = maxAddonPlanLicense;
        const maxLicenseAddonTypeNameKey = this.plansofBizBundle.maxLicensesAddonPlan.typeNameKey;
        const maxLicenseAddonTypeName = this.$t(maxLicenseAddonTypeNameKey);
        reason = this.$t('billing.meeting_license_decrease_tips_need_decrease_addon', { product: maxLicenseAddonTypeName }).replace(ZoomWorkplaceRebrandingReg, ZOOM_WORKPLACE);
        if (window.newBillingConfig?.sessionToWebinarPlusRename) {
          reason = reason.replace(zoomSessionsRebrandingReg, ZOOM_WEBINARS_PLUS);
        }
      }

      return {
        min,
        reason
      };
    },
    disableDownSell() {
      return this?.basicConfig?.downSellData?.disableDownSell;
    },
    additionalSpans() {
      return {
        proplus: [
          {
            value: 1,
            text: '',
          },
          {
            value: this.maxHost,
            text: this.$t('billing.zone.max_of_plans_user', [this.maxHost, this.maxHost + 1]),
          },
        ],
      }
    },
    planBillInfo() {
      const plan = computedCallingPlans(this.boughtPlans)?.[0] ?? {};
      const basePlan = computedPbxBasePlans(this.boughtPlans)?.[0] ?? {};
      const payment = getZPCallingFeesText(basePlan).title;
      const billingCycle = basePlan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO ? BILLING_CYCLE.MONTHLY : plan.billing_cycle;
      return {
        billing_cycle: billingCycle,
        payment
      }
    },
    selectedProplus() {
      return this.getSelectedPlans.find(p => p.type === planTypes.ZONE_PRO_PLUS);
    }
  },
  watch: {
    selectedProplus: {
      handler(nv) {
        this.selectedNumber = nv?.cartInfo?.selected_number ?? 1;
      },
      deep: true,
      immediate: true
    },
    licenseTips(value) {
      if (value) {
        this.$tracking.buyInteraction('banner - product level', `${this.trackingPlanName} - ${value}`);
      }
    }
  },
  methods: {
    i18n_cycle,
    ...mapMutations(['updateSelectPlan']),
    updatePlanLicense(licenseNumber) {
        this.updateSelectPlan({
          plan_code: this.currentEditPlan?.plan_code,
          cartInfo: {
            selected_number: licenseNumber,
          }
        })
    },
    handleLicenseTipsClick(e) {
      if(e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(`${this.trackingPlanName} plan users`, 'the maximum amount is 99 users - contact sales');
      }
    },
    handleAppealCycle(e) {
      if(e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(`${this.trackingPlanName} subscription cycle`, 'contact us');
      }
    },
    handleAttendeeTipShow() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} plan users`, 'select the number of users - tooltip');
    }
  }
};
</script>

<style lang='scss'>
.opc-zone-bundle__main {
  .appeal-cycle {
    margin-top: 24px;
  }

  .reason-tips {
    margin-top: 12px;
  }
}
</style>
